import * as THREE from 'three/webgpu';

/**
 * Procedural animation layers applied after the AnimationMixer each frame (docs/06
 * "Procedural layers"): spring tails, head/neck look-at, and slope adaptation. They add
 * rotations in world space on top of whatever the clips posed, so they work with any clip.
 */

const UP = new THREE.Vector3(0, 1, 0);
const qa = new THREE.Quaternion();
const qb = new THREE.Quaternion();
const qParent = new THREE.Quaternion();
const axis = new THREE.Vector3();

/** Rotate `bone` by `angle` around a world-space axis, keeping the rest of the pose. */
function rotateWorld(bone: THREE.Object3D, worldAxis: THREE.Vector3, angle: number) {
  if (!angle || !bone.parent) return;
  bone.parent.getWorldQuaternion(qParent);
  axis.copy(worldAxis).applyQuaternion(qa.copy(qParent).invert()).normalize();
  qb.setFromAxisAngle(axis, angle);
  bone.quaternion.premultiply(qb);
  bone.updateMatrixWorld(true);
}

export interface SpringChainTuning {
  /** Radians of swing per rad/s of body yaw rate. */
  yawGain: number;
  /** Radians of lift per m/s of vertical speed (tail rises while falling). */
  pitchGain: number;
  stiffness: number;
  damping: number;
  maxAngle: number;
}

export const TAIL_TUNING: SpringChainTuning = {
  yawGain: 0.09,
  pitchGain: 0.035,
  stiffness: 70,
  damping: 9,
  maxAngle: 0.5,
};

/** A damped spring per bone; each link is looser than its parent, so the tip lags and whips. */
export function createSpringChain(bones: THREE.Object3D[], tuning = TAIL_TUNING) {
  const state = bones.map(() => ({ yaw: 0, yawV: 0, pitch: 0, pitchV: 0 }));
  const lateral = new THREE.Vector3();
  return {
    tuning,
    apply(dt: number, yawRate: number, vy: number, facing: number) {
      if (!bones.length || dt <= 0) return;
      const h = Math.min(dt, 1 / 30);
      lateral.set(Math.cos(facing), 0, -Math.sin(facing));
      bones.forEach((bone, i) => {
        const s = state[i] as (typeof state)[number];
        const loose = 1 / (1 + i * 0.6);
        const k = tuning.stiffness * loose;
        const c = tuning.damping * Math.sqrt(loose);
        const targetYaw = THREE.MathUtils.clamp(
          -yawRate * tuning.yawGain,
          -tuning.maxAngle,
          tuning.maxAngle,
        );
        const targetPitch = THREE.MathUtils.clamp(
          -vy * tuning.pitchGain,
          -tuning.maxAngle,
          tuning.maxAngle,
        );
        s.yawV += (k * (targetYaw - s.yaw) - c * s.yawV) * h;
        s.yaw += s.yawV * h;
        s.pitchV += (k * (targetPitch - s.pitch) - c * s.pitchV) * h;
        s.pitch += s.pitchV * h;
        rotateWorld(bone, UP, s.yaw);
        rotateWorld(bone, lateral, s.pitch);
      });
    },
  };
}

export interface LookTuning {
  maxYaw: number;
  maxPitch: number;
  speed: number;
  /** Share of the turn taken by each bone, neck first; the rest goes to the head. */
  weights: number[];
}

/** Turn the neck chain and head toward a world direction, clamped and smoothed. */
export function createLookAt(bones: THREE.Object3D[], tuning: LookTuning) {
  let yaw = 0;
  let pitch = 0;
  const lateral = new THREE.Vector3();
  return {
    tuning,
    apply(
      dt: number,
      facing: number,
      target: { yaw: number; pitch: number } | null,
      weight: number,
    ) {
      let wantYaw = 0;
      let wantPitch = 0;
      if (target) {
        wantYaw = Math.atan2(Math.sin(target.yaw - facing), Math.cos(target.yaw - facing));
        // Past ~110° behind, looking back over the shoulder reads wrong; ease back to center.
        if (Math.abs(wantYaw) > 1.9) wantYaw = 0;
        wantYaw = THREE.MathUtils.clamp(wantYaw, -tuning.maxYaw, tuning.maxYaw) * weight;
        wantPitch = THREE.MathUtils.clamp(target.pitch, -tuning.maxPitch, tuning.maxPitch) * weight;
      }
      const a = Math.min(1, dt * tuning.speed);
      yaw += (wantYaw - yaw) * a;
      pitch += (wantPitch - pitch) * a;
      lateral.set(Math.cos(facing + yaw), 0, -Math.sin(facing + yaw));
      bones.forEach((bone, i) => {
        const w = tuning.weights[i] ?? 0;
        rotateWorld(bone, UP, yaw * w);
        rotateWorld(bone, lateral, -pitch * w);
      });
    },
  };
}

/** Body pitch/roll that follows the ground slope, from four height samples. */
export function createSlopeAdapter(halfLength: number, halfWidth: number) {
  let pitch = 0;
  let roll = 0;
  return {
    update(
      dt: number,
      pos: { x: number; y: number; z: number },
      facing: number,
      ground: (x: number, y: number, z: number) => number | null,
      grounded: boolean,
    ) {
      let wantPitch = 0;
      let wantRoll = 0;
      if (grounded) {
        const fx = Math.sin(facing);
        const fz = Math.cos(facing);
        const rx = -fz;
        const rz = fx;
        const top = pos.y + 1.5;
        const sample = (dx: number, dz: number) => ground(pos.x + dx, top, pos.z + dz) ?? pos.y;
        const front = sample(fx * halfLength, fz * halfLength);
        const back = sample(-fx * halfLength, -fz * halfLength);
        const right = sample(rx * halfWidth, rz * halfWidth);
        const left = sample(-rx * halfWidth, -rz * halfWidth);
        wantPitch = THREE.MathUtils.clamp(Math.atan2(back - front, halfLength * 2), -0.45, 0.45);
        wantRoll = THREE.MathUtils.clamp(Math.atan2(right - left, halfWidth * 2), -0.3, 0.3);
      }
      const a = Math.min(1, dt * 10);
      pitch += (wantPitch - pitch) * a;
      roll += (wantRoll - roll) * a;
      return { pitch, roll };
    },
  };
}
